import React, { useState } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { PublicKey } from '@solana/web3.js';
import { getAssociatedTokenAddress, mintTo, getMint } from '@solana/spl-token';
import { useProgramContext } from '../contexts/ProgramContext';

const AdminPanel: React.FC = () => {
  const { publicKey } = useWallet();
  const { connection } = useConnection();
  const { program } = useProgramContext();
  const [loading, setLoading] = useState(false);

  // Admin management
  const [newAdminAddress, setNewAdminAddress] = useState('');
  const [removeAdminAddress, setRemoveAdminAddress] = useState('');

  // Token airdrop
  const [airdropRecipient, setAirdropRecipient] = useState('');
  const [airdropAmount, setAirdropAmount] = useState('');
  const [tokenMint, setTokenMint] = useState('');

  // Status messages
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const addAdmin = async () => {
    if (!program || !publicKey || !newAdminAddress) {
      setError('Please connect wallet and enter admin address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const newAdmin = new PublicKey(newAdminAddress);


      const tx = await program.methods
        .addAdmin(newAdmin)
        .accounts({})
        .rpc();

      setSuccess(`Admin added successfully! Transaction: ${tx.slice(0, 8)}...`);
      setNewAdminAddress('');
    } catch (err: any) {
      console.error('Error adding admin:', err);
      setError(err.message || 'Error adding admin');
    }
    setLoading(false);
  };

  const removeAdmin = async () => {
    if (!program || !publicKey || !removeAdminAddress) {
      setError('Please connect wallet and enter admin address to remove');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const adminToRemove = new PublicKey(removeAdminAddress);


      const tx = await program.methods
        .removeAdmin(adminToRemove)
        .accounts({})
        .rpc();

      setSuccess(`Admin removed successfully! Transaction: ${tx.slice(0, 8)}...`);
      setRemoveAdminAddress('');
    } catch (err: any) {
      console.error('Error removing admin:', err);
      setError(err.message || 'Error removing admin');
    }
    setLoading(false);
  };

  const airdropTokens = async () => {
    if (!publicKey || !airdropRecipient || !airdropAmount || !tokenMint) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const mintPubkey = new PublicKey(tokenMint);
      const recipientPubkey = new PublicKey(airdropRecipient);

      // Get mint info to get decimals
      const mintInfo = await getMint(connection, mintPubkey);
      const decimals = mintInfo.decimals;

      // Calculate amount with decimals
      const amount = BigInt(parseFloat(airdropAmount) * Math.pow(10, decimals));

      // Get or create recipient's token account
      const recipientAta = await getAssociatedTokenAddress(
        mintPubkey,
        recipientPubkey
      );

      // Check if account exists
      const accountInfo = await connection.getAccountInfo(recipientAta);

      if (!accountInfo) {
        setError('Recipient token account does not exist. Please ask recipient to create their token account first, or you need to send a transaction to create it');
        setLoading(false);
        return;
      }

      // Mint tokens (requires mint authority)
      const signature = await mintTo(
        connection,
        publicKey as any,
        mintPubkey,
        recipientAta,
        publicKey,
        amount
      );

      setSuccess(`Tokens airdropped successfully! Transaction: ${signature.slice(0, 8)}...`);
      setAirdropRecipient('');
      setAirdropAmount('');
    } catch (err: any) {
      console.error('Error airdropping tokens:', err);
      setError(err.message || 'Error airdropping tokens');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-habitat-neutral-900">Admin Panel</h2>
        <div className="mt-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-habitat">
          <p className="text-sm text-yellow-800 font-medium">Admin functions - use with caution!</p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-habitat">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-habitat">
          {success}
        </div>
      )}

      {/* Admin Management Section */}
      <div className="card">
        <h3 className="text-xl font-bold text-habitat-neutral-900 mb-6">Admin Management</h3>

        <div className="space-y-6">
          {/* Add Admin */}
          <div>
            <h4 className="text-lg font-semibold text-habitat-primary mb-3">Add New Admin</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-habitat-neutral-700 mb-2">
                  New Admin Address
                </label>
                <input
                  type="text"
                  value={newAdminAddress}
                  onChange={(e) => setNewAdminAddress(e.target.value)}
                  placeholder="Enter new admin public key"
                  className="input-field"
                />
              </div>
              <button
                onClick={addAdmin}
                disabled={loading || !newAdminAddress}
                className={`btn-primary ${loading || !newAdminAddress ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loading ? 'Adding...' : 'Add Admin'}
              </button>
            </div>
          </div>

          {/* Remove Admin */}
          <div className="pt-6 border-t border-habitat-neutral-200">
            <h4 className="text-lg font-semibold text-red-600 mb-3">Remove Admin</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-habitat-neutral-700 mb-2">
                  Admin Address to Remove
                </label>
                <input
                  type="text"
                  value={removeAdminAddress}
                  onChange={(e) => setRemoveAdminAddress(e.target.value)}
                  placeholder="Enter admin public key to remove"
                  className="input-field"
                />
              </div>
              <button
                onClick={removeAdmin}
                disabled={loading || !removeAdminAddress}
                className={`bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-habitat transition-colors duration-200 ${
                  loading || !removeAdminAddress ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Removing...' : 'Remove Admin'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Token Airdrop Section */}
      <div className="card">
        <h3 className="text-xl font-bold text-habitat-neutral-900 mb-4">Token Airdrop</h3>
        <p className="text-sm text-habitat-neutral-600 mb-6">
          Send tokens to any address (requires mint authority)
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-habitat-neutral-700 mb-2">
              Token Mint Address
            </label>
            <input
              type="text"
              value={tokenMint}
              onChange={(e) => setTokenMint(e.target.value)}
              placeholder="Enter token mint address"
              className="input-field"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-habitat-neutral-700 mb-2">
              Recipient Address
            </label>
            <input
              type="text"
              value={airdropRecipient}
              onChange={(e) => setAirdropRecipient(e.target.value)}
              placeholder="Enter recipient wallet address"
              className="input-field"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-habitat-neutral-700 mb-2">
              Amount
            </label>
            <input
              type="number"
              value={airdropAmount}
              onChange={(e) => setAirdropAmount(e.target.value)}
              placeholder="Enter amount (e.g., 1000)"
              step="0.000000001"
              className="input-field"
            />
          </div>

          <button
            onClick={airdropTokens}
            disabled={loading || !airdropRecipient || !airdropAmount || !tokenMint}
            className={`btn-secondary ${
              loading || !airdropRecipient || !airdropAmount || !tokenMint
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
          >
            {loading ? 'Sending...' : 'Airdrop Tokens'}
          </button>
        </div>
      </div>

      {/* Guidelines */}
      <div className="card bg-habitat-neutral-50">
        <h3 className="font-semibold text-habitat-neutral-900 mb-3">Admin Guidelines</h3>
        <ul className="space-y-2 text-sm text-habitat-neutral-600">
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Only super admin can add/remove other admins</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Super admin cannot be removed</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Maximum of 10 admins allowed</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>All admin actions are logged on-chain</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Token airdrop requires you to have mint authority for the specified token</span>
          </li>
        </ul>
      </div>

      {/* Program Info */}
      <div className="card bg-habitat-neutral-50">
        <h3 className="font-semibold text-habitat-neutral-900 mb-3">Program Information</h3>
        <ul className="space-y-2 text-sm text-habitat-neutral-600">
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Pause/unpause functionality is not available in the current program version</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Contact developers if additional admin controls are needed</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default AdminPanel;
